/**
 * Brands — manage page.
 *
 * DataTable + Sheet CRUD over persistent mock state for product brands.
 * Soft-delete sets isActive to false so historical references keep working.
 */

import { useMemo, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Tags, Trash2, Undo2 } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockBrands,
  type MockBrand,
} from "../../../lib/catalogMock";

export const Route = createFileRoute("/_app/settings/brands")({
  component: BrandsPage,
});

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function BrandsPage() {
  const [brands, setBrands] = usePersistentMockState<MockBrand[]>(
    "brands",
    mockBrands,
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<MockBrand | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<MockBrand | null>(null);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: MockBrand) {
    setEditing(row);
    setSheetOpen(true);
  }

  function handleSubmit(values: BrandFormValues) {
    if (editing) {
      setBrands((prev) =>
        prev.map((b) =>
          b._id === editing._id
            ? {
                ...b,
                displayName: values.displayName,
                description: values.description || null,
                isActive: values.isActive,
              }
            : b,
        ),
      );
      toast.success("Brand updated. Audit trail recorded.");
    } else {
      const newId = `brand_${Date.now()}`;
      setBrands((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          description: values.description || null,
          isActive: values.isActive,
        },
      ]);
      toast.success("Brand created (mock)");
    }
    setSheetOpen(false);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    const target = archiveTarget;
    setBrands((prev) =>
      prev.map((b) =>
        b._id === target._id ? { ...b, isActive: false } : b,
      ),
    );
    setArchiveTarget(null);
    toast.success("Brand archived");
  }

  function handleRestore(row: MockBrand) {
    setBrands((prev) =>
      prev.map((b) =>
        b._id === row._id ? { ...b, isActive: true } : b,
      ),
    );
    toast.success("Brand restored");
  }

  const columns = useMemo<DataTableColumn<MockBrand>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        cell: (info) => (
          <span className="text-(--text-secondary) truncate max-w-[300px] block">
            {info.row.original.description ?? "—"}
          </span>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => (
          <Badge
            variant={info.row.original.isActive ? "success" : "neutral"}
            size="sm"
          >
            {info.row.original.isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1 justify-end">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(row);
                }}
                aria-label="Edit"
              >
                <Pencil className="size-3.5" />
              </Button>
              {row.isActive ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArchiveTarget(row);
                  }}
                  aria-label="Archive"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(row);
                  }}
                  aria-label="Restore"
                >
                  <Undo2 className="size-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Brands"
        description="Brand catalog tagged on each product. Manage brand entries so product forms stay consistent."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add brand
          </Button>
        }
      />

      <DataTable
        data={brands}
        columns={columns}
        emptyTitle="No brands yet"
        emptyDescription="Add your first brand to tag products in the catalog."
        emptyIcon={<Tags className="size-5 text-(--text-tertiary)" />}
        emptyAction={
          <Button onClick={openCreate}>
            <Plus className="size-4" /> Add brand
          </Button>
        }
      />

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>
              {editing ? "Edit brand" : "New brand"}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the brand details. Code is locked once created."
                : "Brand codes appear on product records — keep them short and stable."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <BrandForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      description: editing.description ?? "",
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Archive confirmation dialog */}
      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              Archive {archiveTarget?.displayName}?
            </DialogTitle>
            <DialogDescription>
              This brand will be marked inactive and hidden from new product
              assignments. Existing products referencing it will keep their data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmArchive}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Inline form
 * ───────────────────────────────────────────────────────────── */

type BrandFormValues = {
  code: string;
  displayName: string;
  description: string;
  isActive: boolean;
};

function BrandForm({
  initialValues,
  isEdit = false,
  onSubmit,
  onCancel,
}: {
  initialValues?: Partial<BrandFormValues>;
  isEdit?: boolean;
  onSubmit: (values: BrandFormValues) => void;
  onCancel?: () => void;
}) {
  const [code, setCode] = useState(initialValues?.code ?? "");
  const [displayName, setDisplayName] = useState(
    initialValues?.displayName ?? "",
  );
  const [description, setDescription] = useState(
    initialValues?.description ?? "",
  );
  const [isActive, setIsActive] = useState(initialValues?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!isEdit && !code.trim()) errs.code = "Code is required.";
    if (!displayName.trim()) errs.displayName = "Name is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({
      code: code.trim().toUpperCase(),
      displayName: displayName.trim(),
      description: description.trim(),
      isActive,
    });
  }

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormItem>
        <FormLabel>Code</FormLabel>
        <FormControl>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. SAMSUNG"
            disabled={isEdit}
          />
        </FormControl>
        {errors.code ? <FormMessage>{errors.code}</FormMessage> : null}
      </FormItem>

      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Samsung Electronics"
          />
        </FormControl>
        {errors.displayName ? (
          <FormMessage>{errors.displayName}</FormMessage>
        ) : null}
      </FormItem>

      <FormItem>
        <FormLabel>Description</FormLabel>
        <FormControl>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional brand description"
            rows={3}
            className="flex min-h-[80px] w-full rounded-md border border-(--border-default) bg-(--bg-input) px-3 py-2 text-[13px] text-(--text-primary) placeholder:text-(--text-tertiary) focus:outline-none focus:ring-2 focus:ring-(--ring-focus) disabled:cursor-not-allowed disabled:opacity-50"
          />
        </FormControl>
      </FormItem>

      <FormItem className="flex items-center justify-between">
        <FormLabel>Active</FormLabel>
        <Switch checked={isActive} onCheckedChange={setIsActive} />
      </FormItem>

      <div className="flex justify-end gap-2 pt-4 border-t border-(--border-subtle)">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit">
          {isEdit ? "Save changes" : "Create brand"}
        </Button>
      </div>
    </Form>
  );
}
